import React, { useEffect, useState, ChangeEvent } from "react";
import { useSelector } from "react-redux";
import { State } from "../../state";
import employeeDeficitService from "../../services/employeeDeficitService";
import { 
  EmployeeDeficitGetDTO,
  EmployeeDeficitIsDeductedPostDTO,
  EmployeeDeficitFilter
} from "../../typings/employeeDeficit";
import { Card, Table, Form, Button, Badge, Row, Col, Spinner, Modal } from "react-bootstrap";
import { Link } from "react-router-dom";
import { FaMoneyBillWave, FaCheck, FaTimes, FaEye } from "react-icons/fa";

export default function EmployeeDeficit() {
  const languageState = useSelector((state: State) => state.languageReducer);
  // Access the language data directly without the extra .data property
  const language = languageState; 
  
  const [loading, setLoading] = useState<boolean>(false);
  const [deficits, setDeficits] = useState<EmployeeDeficitGetDTO[]>([]);
  const [filter, setFilter] = useState<EmployeeDeficitFilter>({ isDeducted: false });
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [confirmAction, setConfirmAction] = useState<{ 
    deficitId: number, 
    isDeducted: boolean 
  } | null>(null);

  useEffect(() => {
    loadDeficits();
  }, []);

  const loadDeficits = async () => {
    setLoading(true);
    try {
      const response = await employeeDeficitService.getEmployeeDeficits(
        filter.startShiftId,
        filter.endShiftId,
        filter.isDeducted
      );
      setDeficits(response.data || []);
    } catch (error) {
      console.error("Failed to load employee deficits:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e: ChangeEvent<HTMLElement>) => {
    const target = e.target as HTMLInputElement | HTMLSelectElement;
    const { name, value } = target;
    let updatedValue: any = value;
    
    // Handle boolean conversion for isDeducted
    if (name === "isDeducted") {
      if (value === "all") {
        updatedValue = undefined;
      } else if (value === "true") {
        updatedValue = true;
      } else if (value === "false") {
        updatedValue = false;
      }
    } else if (value === "") {
      updatedValue = undefined;
    } else if (!isNaN(Number(value))) {
      updatedValue = Number(value);
    }
    
    setFilter(prev => ({
      ...prev,
      [name]: updatedValue
    }));
  };

  const handleApplyFilter = (e: React.FormEvent) => {
    e.preventDefault();
    loadDeficits();
  };

  const resetFilter = () => {
    setFilter({ isDeducted: false });
    setTimeout(() => {
      loadDeficits();
    }, 0);
  };

  const handleStatusChange = (deficitId: number, isDeducted: boolean) => {
    setConfirmAction({ deficitId, isDeducted });
    setShowConfirmModal(true);
  };

  const confirmStatusChange = async () => {
    setShowConfirmModal(false);
    
    if (!confirmAction) return;
    
    setLoading(true);
    try {
      const updateData: EmployeeDeficitIsDeductedPostDTO[] = [{
        shiftEmployeeDeficitEntityId: confirmAction.deficitId,
        deducted: confirmAction.isDeducted
      }];
      
      await employeeDeficitService.updateIsDeducted(updateData);
      loadDeficits();
    } catch (error) {
      console.error("Failed to update deficit status:", error);
    } finally {
      setLoading(false);
      setConfirmAction(null);
    }
  };

  // Format the deficit amount with a null check
  const formatAmount = (amount: number | undefined | null) => {
    return amount !== undefined && amount !== null 
      ? amount.toFixed(2)
      : "0.00";
  };

  // Helper function to get amount from deficit object
  const getDeficitAmount = (deficit: EmployeeDeficitGetDTO): number | undefined | null => {
    return deficit.amount !== undefined ? deficit.amount : deficit.deficitAmount;
  };

  // Helper function to safely get text with fallback
  const getText = (path: string, fallback: string) => {
    try {
      // Split the path by dots
      const keys = path.split('.');
      let value: any = language;
      
      // Navigate through the object
      for (const key of keys) {
        if (value === undefined || value === null) return fallback;
        value = value[key as keyof typeof value];
      }
      
      return value || fallback;
    } catch (error) {
      console.error(`Error getting text for path: ${path}`, error);
      return fallback;
    }
  };

  // Use ts-ignore to suppress the icon errors
  return (
    <div className="container mt-4 mb-5">
      <h2 className="mb-4 d-flex align-items-center" style={{ 
        color: 'var(--primary-dark, #2c3e50)', 
        fontWeight: 600,
        fontSize: '1.8rem'
      }}>
        <span className="me-3 d-flex align-items-center justify-content-center" style={{
          background: 'var(--primary-color, #4361ee)',
          color: 'white',
          borderRadius: '50%',
          width: '40px',
          height: '40px',
          boxShadow: '0 4px 6px rgba(67, 97, 238, 0.2)'
        }}>
          {/* @ts-ignore */}
          <FaMoneyBillWave />
        </span> 
        {language?.data?.employeeDeficit?.title || "Employee Deficits"}
      </h2>

      {/* Filter Card */}
      <Card className="mb-4 shadow-sm border-0" style={{ borderRadius: '12px', overflow: 'hidden' }}>
        <Card.Header as="h5" style={{ 
          background: 'var(--primary-light, #edf2f7)', 
          borderBottom: 'none',
          padding: '1rem 1.5rem',
          color: 'var(--primary-dark, #2c3e50)',
          fontWeight: 600
        }}>
          {language?.data?.employeeDeficit?.filter?.title || "Filter"}
        </Card.Header>
        <Card.Body style={{ padding: '1.5rem' }}>
          <Form onSubmit={handleApplyFilter}>
            <Row>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: 500 }}>{language?.data?.employeeDeficit?.filter?.startShift || "Start Shift"}</Form.Label>
                  <Form.Control 
                    type="number" 
                    name="startShiftId" 
                    value={filter.startShiftId || ""}
                    onChange={handleFilterChange}
                    style={{ 
                      borderRadius: '8px', 
                      padding: '0.75rem 1rem',
                      border: '1px solid var(--border-color, #e2e8f0)',
                      boxShadow: 'none',
                      transition: 'border-color 0.2s, box-shadow 0.2s'
                    }}
                    placeholder="Enter shift ID"
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: 500 }}>{language?.data?.employeeDeficit?.filter?.endShift || "End Shift"}</Form.Label>
                  <Form.Control 
                    type="number" 
                    name="endShiftId" 
                    value={filter.endShiftId || ""}
                    onChange={handleFilterChange}
                    style={{ 
                      borderRadius: '8px', 
                      padding: '0.75rem 1rem',
                      border: '1px solid var(--border-color, #e2e8f0)',
                      boxShadow: 'none',
                      transition: 'border-color 0.2s, box-shadow 0.2s'
                    }}
                    placeholder="Enter shift ID"
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: 500 }}>{language?.data?.employeeDeficit?.filter?.deductionStatus || "Deduction Status"}</Form.Label>
                  <Form.Select 
                    name="isDeducted" 
                    value={filter.isDeducted === undefined ? "all" : filter.isDeducted.toString()}
                    onChange={handleFilterChange}
                    style={{ 
                      borderRadius: '8px', 
                      padding: '0.75rem 1rem',
                      border: '1px solid var(--border-color, #e2e8f0)',
                      boxShadow: 'none',
                      transition: 'border-color 0.2s, box-shadow 0.2s'
                    }}
                  >
                    <option value="all">{language?.data?.employeeDeficit?.filter?.allDeficits || "All Deficits"}</option>
                    <option value="true">{language?.data?.employeeDeficit?.filter?.deducted || "Deducted"}</option>
                    <option value="false">{language?.data?.employeeDeficit?.filter?.notDeducted || "Not Deducted"}</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={3} className="d-flex align-items-end justify-content-start mb-3">
                <div className="d-flex gap-3">
                  <Button 
                    variant="primary" 
                    type="submit"
                    style={{
                      borderRadius: '8px',
                      padding: '0.75rem 1.5rem',
                      background: 'var(--primary-color, #4361ee)',
                      border: 'none',
                      fontWeight: 500,
                      boxShadow: '0 4px 6px rgba(67, 97, 238, 0.15)',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {language?.data?.employeeDeficit?.filter?.applyFilter || "Filter"}
                  </Button>
                  <Button 
                    variant="light" 
                    onClick={resetFilter}
                    style={{
                      borderRadius: '8px',
                      padding: '0.75rem 1.5rem',
                      background: 'var(--light-color, #f8f9fa)',
                      border: '1px solid var(--border-color, #e2e8f0)',
                      color: 'var(--text-color, #4a5568)',
                      fontWeight: 500,
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {language?.data?.employeeDeficit?.filter?.resetFilter || "Reset"}
                  </Button>
                </div>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>

      {/* Deficits Table */}
      <Card className="shadow-sm border-0" style={{ borderRadius: '12px', overflow: 'hidden' }}>
        <Card.Body style={{ padding: '1.5rem' }}>
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" style={{ color: 'var(--primary-color, #4361ee)' }} />
              <p className="mt-3" style={{ color: 'var(--text-color, #4a5568)' }}>
                {language?.data?.employeeDeficit?.loading || "Loading..."}
              </p>
            </div>
          ) : deficits.length === 0 ? (
            <div className="text-center py-5">
              <p style={{ color: 'var(--text-color, #4a5568)' }}>
                {language?.data?.employeeDeficit?.noDeficits || "No deficits found."}
              </p>
            </div>
          ) : (
            <div className="table-responsive">
              <Table striped hover style={{ 
                borderCollapse: 'separate', 
                borderSpacing: '0 5px'
              }}>
                <thead>
                  <tr style={{ 
                    background: 'var(--primary-light, #edf2f7)',
                  }}>
                    <th style={{ padding: '1rem', fontWeight: 600, color: 'var(--primary-dark, #2c3e50)', borderTop: 'none' }}>
                      {language?.data?.employeeDeficit?.table?.id || "ID"}
                    </th>
                    <th style={{ padding: '1rem', fontWeight: 600, color: 'var(--primary-dark, #2c3e50)', borderTop: 'none' }}>
                      {language?.data?.employeeDeficit?.table?.shiftId || "Shift ID"}
                    </th>
                    <th style={{ padding: '1rem', fontWeight: 600, color: 'var(--primary-dark, #2c3e50)', borderTop: 'none' }}>
                      {language?.data?.employeeDeficit?.table?.employee || "Employee"}
                    </th>
                    <th style={{ padding: '1rem', fontWeight: 600, color: 'var(--primary-dark, #2c3e50)', borderTop: 'none' }}>
                      {language?.data?.employeeDeficit?.table?.amount || "Amount"}
                    </th>
                    <th style={{ padding: '1rem', fontWeight: 600, color: 'var(--primary-dark, #2c3e50)', borderTop: 'none' }}>
                      {language?.data?.employeeDeficit?.table?.status || "Status"}
                    </th>
                    <th style={{ padding: '1rem', fontWeight: 600, color: 'var(--primary-dark, #2c3e50)', borderTop: 'none' }}>
                      {language?.data?.employeeDeficit?.table?.actions || "Actions"}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {deficits.map((deficit) => (
                    <tr key={deficit.shiftEmployeeDeficitEntityId || deficit.id} style={{ 
                      background: 'white',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                      transition: 'transform 0.1s ease-in-out'
                    }}>
                      <td style={{ padding: '1rem', borderTop: 'none' }}>{deficit.shiftEmployeeDeficitEntityId || deficit.id}</td>
                      <td style={{ padding: '1rem', borderTop: 'none' }}>{deficit.shiftId}</td>
                      <td style={{ padding: '1rem', borderTop: 'none', fontWeight: 500 }}>{deficit.employeeName}</td>
                      <td style={{ padding: '1rem', borderTop: 'none', textAlign: 'right', fontWeight: 600 }}>
                        {formatAmount(getDeficitAmount(deficit))}
                      </td>
                      <td style={{ padding: '1rem', borderTop: 'none' }}>
                        <Badge 
                          bg={(deficit.deducted || deficit.isDeducted) ? "success" : "danger"}
                          style={{ 
                            padding: '0.5rem 0.75rem', 
                            borderRadius: '6px',
                            fontWeight: 500,
                            fontSize: '0.85rem'
                          }}
                        >
                          {(deficit.deducted || deficit.isDeducted) 
                            ? (language?.data?.employeeDeficit?.status?.deducted || "Deducted") 
                            : (language?.data?.employeeDeficit?.status?.notDeducted || "Not Deducted")}
                        </Badge>
                      </td>
                      <td style={{ padding: '1rem', borderTop: 'none' }}>
                        <div className="d-flex gap-2">
                          <Link 
                            to={`/report/shift?shiftId=${deficit.shiftId}`} 
                            className="btn btn-sm" 
                            style={{
                              background: 'var(--info-color, #3498db)',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              padding: '0.5rem',
                              boxShadow: '0 2px 4px rgba(52, 152, 219, 0.2)',
                              transition: 'all 0.2s ease'
                            }}
                            title={language?.data?.employeeDeficit?.buttons?.goToShift || "Go To Shift"}
                          >
                            {/* @ts-ignore */}
                            <FaEye />
                          </Link>
                          {(deficit.deducted || deficit.isDeducted) ? (
                            <Button 
                              style={{
                                background: 'var(--warning-color, #f1c40f)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                padding: '0.5rem',
                                boxShadow: '0 2px 4px rgba(241, 196, 15, 0.2)',
                                transition: 'all 0.2s ease'
                              }}
                              size="sm"
                              onClick={() => {
                                const id = deficit.shiftEmployeeDeficitEntityId || deficit.id;
                                if (id) handleStatusChange(id, false);
                              }}
                              title={language?.data?.employeeDeficit?.buttons?.markAsNotDeducted || "Mark as Not Deducted"}
                            >
                              {/* @ts-ignore */}
                              <FaTimes />
                            </Button>
                          ) : (
                            <Button 
                              style={{
                                background: 'var(--success-color, #2ecc71)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                padding: '0.5rem',
                                boxShadow: '0 2px 4px rgba(46, 204, 113, 0.2)',
                                transition: 'all 0.2s ease'
                              }}
                              size="sm"
                              onClick={() => {
                                const id = deficit.shiftEmployeeDeficitEntityId || deficit.id;
                                if (id) handleStatusChange(id, true);
                              }}
                              title={language?.data?.employeeDeficit?.buttons?.markAsDeducted || "Mark as Deducted"}
                            >
                              {/* @ts-ignore */}
                              <FaCheck />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Confirmation Modal */}
      <Modal 
        show={showConfirmModal} 
        onHide={() => setShowConfirmModal(false)}
        centered
        backdrop="static"
      >
        <Modal.Header 
          closeButton 
          style={{ 
            border: 'none', 
            padding: '1.5rem 1.5rem 0.5rem'
          }}
        >
          <Modal.Title style={{ 
            color: 'var(--primary-dark, #2c3e50)',
            fontWeight: 600
          }}>
            {language?.data?.employeeDeficit?.confirmation?.title || "Confirm"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '1rem 1.5rem' }}>
          {confirmAction?.isDeducted 
            ? (language?.data?.employeeDeficit?.confirmation?.markAsDeducted || "Are you sure you want to mark this deficit as deducted?")
            : (language?.data?.employeeDeficit?.confirmation?.markAsNotDeducted || "Are you sure you want to mark this deficit as not deducted?")}
        </Modal.Body>
        <Modal.Footer style={{ 
          border: 'none',
          padding: '0.5rem 1.5rem 1.5rem'
        }}>
          <Button 
            variant="light" 
            onClick={() => setShowConfirmModal(false)}
            style={{
              borderRadius: '8px',
              padding: '0.6rem 1.2rem',
              background: 'var(--light-color, #f8f9fa)',
              border: '1px solid var(--border-color, #e2e8f0)',
              color: 'var(--text-color, #4a5568)',
              fontWeight: 500,
              transition: 'all 0.2s ease'
            }}
          >
            {language?.data?.employeeDeficit?.confirmation?.no || "No"}
          </Button>
          <Button 
            variant="primary" 
            onClick={confirmStatusChange}
            style={{
              borderRadius: '8px',
              padding: '0.6rem 1.2rem',
              background: confirmAction?.isDeducted ? 'var(--success-color, #2ecc71)' : 'var(--warning-color, #f1c40f)',
              border: 'none',
              fontWeight: 500,
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
              transition: 'all 0.2s ease'
            }}
          >
            {language?.data?.employeeDeficit?.confirmation?.yes || "Yes"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
